import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useCafAnalysisComponentDetailListV5 } from '../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { ICON_CHEVRON_RIGHT_GRAY } from '../../../../../modules/svg/svg'
import urls from '../../../../../modules/urls'
import { tabListMap } from '../../../../webview/caf/analysis/fluency/FluencyTabsSection'
import ThickDivider from '../../../../webview/caf/common/ThickDivider'
import FluencyIntroSection from '../../analysis/fluency/FluencyIntroSection'
import useMetricSidePopup from '../popup/useMetricSidePopup'

interface Props {
  componentId: number
}
const CAFOverviewFluency = ({ componentId }: Props) => {
  const router = useAppRouter()
  useMetricSidePopup('Fluency')
  const { t, locale } = useTranslation()
  const { lessonId, tab } = router.query
  const [activeIndex, setActiveIndex] = useState(-1)
  const { data } = useCafAnalysisComponentDetailListV5(componentId)

  const graphData = useMemo(
    () => data?.metrics.find((metric) => metric.metric_tag === activeIndex),
    [data, activeIndex]
  )
  const withUnit = tabListMap[activeIndex]?.subtitle
  useEffect(() => {
    router.push(
      urls.student.newPortal.lessons.reviewDetail.asCafMetric(
        lessonId,
        3,
        componentId,
        data?.metrics[activeIndex]?.metric_tag || null
      )
    )
  }, [activeIndex, graphData])

  if (!data?.metrics) return null
  return (
    <div className="w-screen px-[16px] sm:w-[424px] sm:px-0">
      <div className="keep-all min-h-screen break-words bg-white pt-[16px] pb-[40px] text-gray-700">
        <div className="mb-[16px] text-[20px] font-bold text-black">{t(n_caf.highlight_list.fluency1)}</div>
        <FluencyIntroSection componentId={componentId} />
        <ThickDivider />
        <div className="mb-[16px] mt-[20px] text-[20px] font-bold text-black">{t(n_caf.fluency.metric)}</div>
        {data.metrics?.map((metric, i) => (
          <ArrowLinkBoxBG
            title={t(tabListMap[metric.metric_tag].title)}
            rightText={tabListMap[metric.metric_tag].subtitle(Number(metric.value))}
            onClick={() => (i === activeIndex ? setActiveIndex(-1) : setActiveIndex(i))}
            active={true}
            key={`${metric.metric_type}_${i}`}
            isSelected={i === activeIndex}
          />
        ))}
      </div>
    </div>
  )
}

interface ArrowLinkBoxBGType {
  title: string | React.ReactFragment | React.ReactElement
  subText?: string | React.ReactFragment | React.ReactElement
  rightText?: string | number
  active?: boolean
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  isSelected: boolean
}

const ArrowLinkBoxBG = ({ title, rightText, active, subText, onClick, isSelected }: ArrowLinkBoxBGType) => {
  return (
    <div
      className={classNames(
        `mb-[8px] flex min-h-[72px] cursor-pointer items-center rounded-[6px] px-[16px] py-[12px] shadow-sm`,
        isSelected ? 'bg-purple-50' : active ? 'bg-white' : 'bg-gray-100',
        isSelected ? 'border-[1.5px] border-purple-500' : 'border-[1px] border-gray-100'
      )}
      onClick={onClick}
    >
      <div className="flex flex-1 items-center justify-between">
        <div>
          <h3
            className={classNames(
              'text-h5 sm:text-[16px]',
              isSelected ? 'text-purple-500' : active ? 'text-black' : 'text-gray-500'
            )}
          >
            {title}
          </h3>
          {subText && <p className="text-[12px] capitalize  text-gray-500">{subText}</p>}
        </div>
        <div className="flex items-center">
          {!!String(rightText) && <span className="text-h5 mr-[16px] text-gray-500 sm:text-[16px]">{rightText}</span>}
          <img src={ICON_CHEVRON_RIGHT_GRAY} alt="chevron" />
        </div>
      </div>
    </div>
  )
}

export default CAFOverviewFluency
